import * as React from "react";
import {
  Button,
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  FormGroup,
  Tab,
  Tabs,
  FormControl,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Alert,
  Switch,
  Chip,
  Stack,
  LinearProgress,
  CircularProgress,
  Checkbox,
  MenuItem,
  Select,
  FormControlLabel,
  TextField,
  IconButton,
  Tooltip,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import SendIcon from "@mui/icons-material/Send";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import LinkIcon from "@mui/icons-material/Link";
import PhoneIcon from "@mui/icons-material/Phone";
import { Spinner } from "react-bootstrap";
import { DataGrid } from "@mui/x-data-grid";
import MUIDataTable from "mui-datatables";

import AccessContainer from "../common/AccessContainer";
import useNotification from "../../hooks/sendNotification";
import { useSessionStorage } from "../../hooks/useSessionStorage";
import EditMergeFields from "./EditMergeFields";
import RcsSuggestions from "./RcsSuggestions";

const uid = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `id_${Math.random().toString(36).slice(2)}_${Date.now()}`;

const Page = () => {
  // ----------------- Constants -----------------
  const maxCharCountSms = 480;
  const maxCharCountRcs = 3072;
  const MERGE_TOKENS = ["{FirstName}", "{LastName}", "{FullName}", "{Role}"];
  const RCS_MAX_BUTTONS = 11;
  const channel = 'RCS';

  const activeElRef = React.useRef(null);
  const setActiveEl = (el) => (activeElRef.current = el);


// NEW — generic caret-aware insert for inputs/textareas/contentEditable
const insertAtCaret = (el, text) => {
  if (!el) return;

  // respect maxLength if present
  const hasMax = typeof el.maxLength === "number" && el.maxLength >= 0;
  if (hasMax && (el.value?.length ?? 0) + text.length > el.maxLength) {
    // trim to fit
    const room = Math.max(0, el.maxLength - (el.value?.length ?? 0));
    text = text.slice(0, room);
  }

  if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) {
    el.focus();
    const start = el.selectionStart ?? el.value.length;
    const end = el.selectionEnd ?? el.value.length;

    if (typeof el.setRangeText === "function") {
      el.setRangeText(text, start, end, "end");
    } else {
      const before = el.value.slice(0, start);
      const after = el.value.slice(end);
      el.value = before + text + after;
      const caret = start + text.length;
      el.setSelectionRange?.(caret, caret);
    }

    // bubble an input event so React/MUI state updates
    el.dispatchEvent(new Event("input", { bubbles: true }));
  } else if (el instanceof HTMLElement && el.isContentEditable) {
    el.focus();
    const sel = window.getSelection?.();
    if (sel && sel.rangeCount > 0 && el.contains(sel.anchorNode)) {
      const range = sel.getRangeAt(0);
      range.deleteContents();
      range.insertNode(document.createTextNode(text));
      sel.removeAllRanges();
      const r = document.createRange();
      const last = el.lastChild;
      if (last) {
        r.setStart(last, (last.textContent || "").length);
        r.collapse(true);
        sel.addRange(r);
      }
    } else {
      el.append(text);
    }
    el.dispatchEvent(new Event("input", { bubbles: true }));
  }
};

// NEW — what EditMergeFields will call instead of setMessageBody
const handleMergeInsert = (incoming) => {
  const el = activeElRef.current || messageRef.current;
  if (!el || typeof incoming !== "string") return;

  // 1) If the component passed only a token, use it directly.
  let token = MERGE_TOKENS.includes(incoming) ? incoming : null;

  // 2) If not, and incoming ends with a known token, use that token.
  if (!token) {
    const endToken = MERGE_TOKENS.find(t => incoming.endsWith(t));
    if (endToken) token = endToken;
  }

  // 3) If still not, strip the messageBody prefix ("value + token" shape).
  if (!token && (messageBody ?? "") && incoming.startsWith(messageBody)) {
    token = incoming.slice((messageBody || "").length);
  }

  // 4) Last-resort: strip the currently focused element's value if it's a prefix.
  if (!token) {
    const current =
      el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement
        ? el.value ?? ""
        : el.isContentEditable
        ? el.textContent ?? ""
        : "";
    token = incoming.startsWith(current) ? incoming.slice(current.length) : incoming;
  }

  // Safety: if this somehow resolves to empty, bail.
  if (!token) return;

  // Enforce length only for the main message textarea.
  if (el === messageRef.current && (messageBody?.length ?? 0) + token.length > maxCharCount) {
    sendNotification({ msg: `Message too long to insert ${token}` });
    return;
  }

  insertAtCaret(el, token);
};


  // ----------------- State -----------------
  const [view, setView] = React.useState("My messages");
  const [groups, setGroups] = React.useState([]);
  const [selectedGroups, setSelectedGroups] = React.useState([]);
  const [groupContacts, setGroupContacts] = React.useState([]);

  const [selectedContacts, setSelectedContacts] = React.useState([]);

  const [isLoadingContacts, setIsLoadingContacts] = React.useState(false);
  const contactsAbortRef = React.useRef(null);

  const [senders, setSenders] = React.useState([]);
  const [sender, setSender] = React.useState("");
  const [tab, setTab] = React.useState("Groups");
  const [pasteList, setPasteList] = React.useState("");
  const [confirmOpen, setConfirmOpen] = React.useState(false);

  const [messageBody, setMessageBody] = React.useState("");
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const [templates, setTemplates] = React.useState([]);
  const [template, setTemplate] = React.useState(null);
  const [messages, setMessages] = React.useState([]);

  const [, sendNotification] = useNotification();
  const [user] = useSessionStorage("user", null);

  const messageRef = React.useRef(null);
  const tableRef = React.useRef(null);
  const [searchText, setSearchText] = React.useState("");

  
  const [rcsButtons, setRcsButtons] = React.useState([]);
  const maxCharCount = channel === "RCS" ? maxCharCountRcs : maxCharCountSms;

  React.useEffect(() => {
    setSelectedContacts([]);
  }, [groupContacts, selectedGroups]);

  // ----------------- Merge field helpers -----------------
  const hasMergeTokens = (text) => MERGE_TOKENS.some((t) => (text || "").includes(t));
  const stripMergeTokens = (text) => MERGE_TOKENS.reduce((acc, t) => acc.split(t).join(""), text || "");

  // ----------------- RCS button helpers -----------------
  const addRcsButton = () => {
    if (rcsButtons.length >= RCS_MAX_BUTTONS) {
      sendNotification({ msg: "RCS allows up to 11 buttons." });
      return;
    }
    setRcsButtons((prev) => [
      ...prev,
      { id: uid(), type: "DIAL_PHONE", text: "", postbackData: "" },
    ]);
  };

  const updateRcsButton = (id, patch) =>
    setRcsButtons((prev) => prev.map((b) => (b.id === id ? { ...b, ...patch } : b)));

  const removeRcsButton = (id) =>
    setRcsButtons((prev) => prev.filter((b) => b.id !== id));

  // ----------------- Validation helpers for custom types -----------------
  const looksLikeE164 = (s) => /^\+?[1-9]\d{6,14}$/.test(s || "");
  const looksLikeUrl = (s) => /^(https?:\/\/)[^\s/$.?#].[^\s]*$/i.test((s || "").trim());

  // ----------------- Columns -----------------
  const messageColumns = [
    {
      field: "message",
      headerName: "Message",
      flex: 2,
      renderCell: (params) => renderMessagePreview(params.value),
    },
    {
      field: "recipients",
      headerName: "Recipients",
      flex: 1,
      renderCell: (params) => (Array.isArray(params.value) ? params.value.length : 0),
    },
    { field: "groups", headerName: "Groups", flex: 1 },
    {
      field: "sent",
      headerName: "Sent",
      flex: 1,
      valueGetter: (params) =>
        params.row._ts ? new Date(params.row._ts * 1000).toLocaleString() : "N/A",
    },
    { field: "user", headerName: "Created by", flex: 1 },
  ];

  const clinicOptions = React.useMemo(() => {
    const all = groupContacts.flatMap((c) =>
      String(c.preferredClinics || "")
        .split(";")
        .map((s) => s.trim())
        .filter(Boolean)
    );
    return Array.from(new Set(all)).sort();
  }, [groupContacts]);

  const groupContactColumns = React.useMemo(
    () => [
      { name: "fullName", label: "Name" },
      {
        name: "number",
        label: "Mobile",
        options: {
          customBodyRender: (val) => (val ? <a href={`tel:${val}`}>{val}</a> : "-"),
        },
      },
      { name: "role", label: "Role" },
      { name: "primaryDistrict", label: "Primary District" },
      {
        name: "preferredClinics",
        label: "Preferred Clinics",
        options: {
          filter: true,
          filterType: "multiselect",
          filterOptions: {
            names: clinicOptions,
            logic: (cellValue, filters) => {
              if (!filters || filters.length === 0) return false; // keep all rows
              const values = String(cellValue || "")
                .split(";")
                .map((s) => s.trim().toLowerCase())
                .filter(Boolean);
              const hasMatch = filters.some((f) =>
                values.some((v) => v.includes(String(f).toLowerCase()))
              );
              return !hasMatch; // exclude if no match
            },
          },
          customFilterListOptions: {
            render: (v) => {
              if (!v) return "";
              const list = Array.isArray(v) ? v : String(v).split(";").map((s) => s.trim());
              return `Clinics: ${list.join(", ")}`;
            },
          },
          customBodyRender: (val) =>
            String(val || "")
              .split(";")
              .map((s) => s.trim())
              .filter(Boolean)
              .join(", "),
        },
      },
    ],
    [clinicOptions]
  );

  // ----------------- Handlers -----------------
  const handleTemplateChange = (event) => {
    const selectedTemplateId = event.target.value;
    const selectedTemplate = templates.find((t) => t.id === selectedTemplateId);
    if (selectedTemplate) {
      setTemplate(selectedTemplateId);
      setMessageBody(selectedTemplate.message);
    }
  };

  const handleGroupToggle = (group) => {
    setSelectedGroups((prev) => {
      const exists = prev.some((g) => g.id === group.id);
      return exists ? prev.filter((g) => g.id !== group.id) : [...prev, group];
    });
  };

  const handleSend = () => {
    const isPasteListInvalid = tab === "Paste List" && !pasteList.trim();
    const theMessageIsTooShort = !messageBody || !sender; 
    const isGroupsInvalid = tab === "Groups" && selectedContacts.length === 0;

    if (theMessageIsTooShort) {
      sendNotification({ msg: "Please fill in all fields" });
      return;
    }

    if (tab === "Paste List" && /{.*?}/.test(messageBody)) {
      sendNotification({
        msg: "Merge fields aren’t supported in Paste List. Please remove any {fields} before sending.",
      });
      return;
    }

    if (isPasteListInvalid || isGroupsInvalid) {
      sendNotification({
        msg:
          tab === "Groups"
            ? "Please select at least one contact"
            : "Please specify recipients",
      });
      return;
    }

    // RCS button validations per type
    if (channel === "RCS") {
      if (rcsButtons.length > RCS_MAX_BUTTONS) {
        sendNotification({ msg: "RCS allows up to 11 buttons." });
        return;
      }
      for (const b of rcsButtons) {
        if (!b.text?.trim() || b.text.length > 25) {
          sendNotification({ msg: "Each RCS button text must be 1–25 characters." });
          return;
        }
        if (b.type === "REPLY") {
          if (!b.postbackData?.trim() || b.postbackData.length > 2048) {
            sendNotification({ msg: "Quick reply needs postback data (1–2048 chars)." });
            return;
          }
        } else if (b.type === "DIAL_PHONE") {
          const num = b.phoneNumber || "";
          if (!looksLikeE164(num)) {
            sendNotification({ msg: "Phone number must be E.164 (e.g., +447911123456)." });
            return;
          }
        } 
        else if (b.type === "OPEN_URL") {
          const u = b.url || "";
          if (!looksLikeUrl(u)) {
            sendNotification({ msg: "URL must start with http(s):// and be valid." });
            return;
          }
        }
      }
    }

    setConfirmOpen(true);
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      setConfirmOpen(false);
      sendNotification({ msg: "Sending message, please wait..." });

      const response = await fetch("/api/MessageHub/CreateMessageHubMessage", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          sender,
          messageBody,               
          pasteList,
          selectedGroups,
          groupContacts: selectedContacts, 
          user: user?.userDetails,
          action: "BULKSMS",
          tab,
          rcsSuggestions:
            channel === "RCS"
              ? rcsButtons.map((b) => {
                  if (b.type === "DIAL_PHONE") {
                    return { type: "DIAL_PHONE", text: b.text, phoneNumber: b.phoneNumber };
                  }
                  if (b.type === "OPEN_URL") {
                    return { type: "OPEN_URL", text: b.text, url: b.url };
                  }
                  return { type: "REPLY", text: b.text, postbackData: b.postbackData };
                })
              : [],
        }),
      });

      if (!response.ok) throw new Error(`Error: ${response.status} ${response.statusText}`);

      const data = await response.json();

      setMessages((prev) => [data, ...prev]);
      setPasteList("");
      setMessageBody("");
      setTemplate(null);
      setSelectedGroups([]);
      setSelectedContacts([]);
      setRcsButtons([]);

      sendNotification({ msg: "Message sent successfully." });
    } catch (error) {
      console.error("Error sending message:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMessageBodyChange = (event) => {
    const value = event.target.value;
    if (value.length > maxCharCount) return;

    // For SMS, enforce GSM characters. For RCS, allow Unicode and length only.
    if (channel === "SMS") {
      const GSM7_BASIC =
        "@£$¥èéùìòÇ\n\rØøÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !\"#¤%&'()*+,-./" +
        "0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿" +
        "abcdefghijklmnopqrstuvwxyzäöñüà";
      const GSM7_EXTENDED = "^{}\\[~]|€";
      const SAFE_CHARS = new Set((GSM7_BASIC + GSM7_EXTENDED).split(""));

      let safeValue = "";
      for (const ch of value) {
        if (ch === "\t") {
          sendNotification({ msg: "⚠️ Removed tab character (not SMS safe)" });
          continue;
        }
        if (SAFE_CHARS.has(ch)) safeValue += ch;
        else sendNotification({ msg: `⚠️ Removed non-safe character: ${ch}` });
      }

      setMessageBody(safeValue);
      setTemplate(null);
      return;
    }

    // RCS path
    setMessageBody(value);
    setTemplate(null);
  };

  const handleViewChange = (e) => {
    setView(e.target.checked ? "My messages" : "All messages");
  };

  const renderMessagePreview = (message) => {
    if (!message) return "No messages yet";
    const words = String(message).split(" ");
    const preview = words.slice(0, 10).join(" ");
    return words.length > 10 ? `${preview}...` : preview;
  };

  const goToMessage = (id) => {
    window.location.href = `/MessageHub/Bulk/${id}`;
  };

  // ----------------- Data fetching -----------------
  const fetchMessages = async () => {
    try {
      const response = await fetch("/api/MessageHub/GetMessageHubMessages", {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          user: user?.userDetails,
        },
        method: "POST",
        body: JSON.stringify({ view }),
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      setMessages(data);
      setTemplates(data);
    } catch (error) {
      console.error("Error fetching messages:", error);
    }
  };

  const fetchGroups = async () => {
    try {
      const response = await fetch("/api/MessageHub/GetMessageHubGroups");
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      setGroups(data);
    } catch (error) {
      console.error("Error fetching groups:", error);
    }
  };

  const fetchSenders = async () => {
    try {
      const response = await fetch("/api/MessageHub/GetMessageHubSenders", {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          user: user?.userDetails,
        },
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      setSenders(data);
      setSender(data?.[0]?.number ?? "");
    } catch (error) {
      console.error("Error fetching numbers:", error);
    }
  };

  React.useEffect(() => {
    fetchGroups();
    fetchMessages();
    fetchSenders();
  }, []);

  React.useEffect(() => {
    fetchMessages();
  }, [view]);

  // Fetch contacts per selected group (with loading + abort)
  React.useEffect(() => {
    if (!selectedGroups || selectedGroups.length === 0) {
      setGroupContacts([]);
      if (contactsAbortRef.current) contactsAbortRef.current.abort();
      setIsLoadingContacts(false);
      return;
    }

    const fetchContacts = async () => {
      try {
        if (contactsAbortRef.current) contactsAbortRef.current.abort();

        const controller = new AbortController();
        contactsAbortRef.current = controller;
        setIsLoadingContacts(true);

        const results = await Promise.all(
          selectedGroups.map(async (group) => {
            const res = await fetch(`/api/FreshCRM/GetFreshCRMContacts/${group.id}`, {
              signal: controller.signal,
            });
            if (!res.ok) throw new Error(`Failed for group ${group.id}`);
            const data = await res.json();

            return data.map((c) => ({
              fullName: c?.display_name ?? "",
              firstName: c?.first_name ?? "",
              lastName: c?.last_name ?? "",
              number: c?.mobile_number ?? "",
              role: c?.custom_field?.cf_locum_type ?? "",
              primaryDistrict: c?.custom_field?.cf_primary_district ?? "",
              preferredClinics: c?.custom_field?.cf_preferred_clinic ?? "",
            }));
          })
        );

        setGroupContacts(results.flat());
      } catch (err) {
        if (err?.name !== "AbortError") console.error("Error fetching contacts:", err);
      } finally {
        setIsLoadingContacts(false);
        contactsAbortRef.current = null;
      }
    };

    fetchContacts();
  }, [selectedGroups]);

  // ----------------- UI helpers (condensed/modern) -----------------
  const sectionCardSx = {
    height: "100%",
    borderRadius: 1.5,
    boxShadow: "none",
    border: "1px solid",
    borderColor: "divider",
    bgcolor: "background.paper",
  };
  const sectionContentSx = {
    p: 1.25,
    gap: 1.25,
    display: "flex",
    flexDirection: "column",
  };
  const subtleBoxSx = {
    borderRadius: 1,
    border: "1px solid",
    borderColor: "divider",
    backgroundColor: "background.paper",
  };
  const labelSx = { color: "text.secondary", fontSize: 11, mb: 0.5, letterSpacing: 0.2 };

  // ----------------- Render -----------------
  return (
    <>
      <AccessContainer
        customStyle={{ maxWidth: "100%", width: "100%" }}
        allowedRoles={["NexusAdmin", "MessageHub"]}
      >
        <Box sx={{ p: 1.5 }}>
          {/* Header */}
          <Box
            sx={{
              mb: 1.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 700, letterSpacing: 0.2 }}>
              Message Hub
            </Typography>

            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                {view === "All messages" ? "All messages" : "My messages"}
              </Typography>
              <FormControl>
                <FormGroup>
                  <FormControlLabel
                    value="My messages"
                    control={<Switch size="small" color="primary" onChange={handleViewChange} defaultChecked />}
                    label=""
                    sx={{ m: 0 }}
                  />
                </FormGroup>
              </FormControl>
            </Box>
          </Box>

          <Grid container spacing={1.25}>
            {/* Left: Recipients */}
            <Grid item xs={12} md={6}>
              <Card sx={sectionCardSx}>
                <CardContent sx={sectionContentSx}>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      Recipients
                    </Typography>
                    <Tabs
                      value={tab}
                      onChange={(e, newValue) => {
                        if (newValue === "Paste List" && hasMergeTokens(messageBody)) {
                          setMessageBody((prev) => stripMergeTokens(prev));
                          sendNotification({
                            msg: "Client fields were removed — Paste List doesn’t support them.",
                          });
                        }
                        setTab(newValue);
                      }}
                      sx={{
                        minHeight: 30,
                        "& .MuiTabs-indicator": { height: 2, borderRadius: 1 },
                        "& .MuiTab-root": { minHeight: 30, px: 1.25, fontSize: 12, textTransform: "none" },
                      }}
                    >
                      <Tab label="Groups" value="Groups" />
                      <Tab label="Paste List" value="Paste List" />
                    </Tabs>
                  </Box>

                  {tab === "Groups" && (
                    <>
                      {/* Group selector */}
                      <Box
                        sx={{
                          ...subtleBoxSx,
                          p: 0.75,
                          maxWidth: "100%",
                          overflowX: "auto",
                          whiteSpace: "nowrap",
                          display: "flex",
                          gap: 0.5,
                          opacity: isLoadingContacts ? 0.6 : 1,
                          pointerEvents: isLoadingContacts ? "none" : "auto",
                        }}
                      >
                        {groups.map((group) => (
                          <Box
                            key={group.id}
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              borderRadius: 1,
                              border: "1px solid",
                              borderColor: "divider",
                              px: 0.75,
                              py: 0.25,
                              backgroundColor: selectedGroups.some((g) => g.id === group.id)
                                ? "action.selected"
                                : "background.paper",
                            }}
                          >
                            <Checkbox
                              checked={selectedGroups.some((g) => g.id === group.id)}
                              onChange={() => handleGroupToggle(group)}
                              disabled={isLoadingContacts}
                              size="small"
                              sx={{ p: 0.25 }}
                            />
                            <Typography variant="caption" noWrap>
                              {group.name}
                            </Typography>
                          </Box>
                        ))}
                      </Box>

                      {/* Contacts table */}
                      <Box sx={{ ...subtleBoxSx, overflow: "hidden", position: "relative" }}>
                        {isLoadingContacts && (
                          <>
                            <LinearProgress
                              sx={{ position: "absolute", top: 0, left: 0, right: 0, zIndex: 2 }}
                            />
                            <Box
                              sx={{
                                position: "absolute",
                                inset: 0,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                bgcolor: "rgba(255,255,255,0.6)",
                                zIndex: 1,
                              }}
                            >
                              <CircularProgress size={24} />
                            </Box>
                          </>
                        )}

                        <MUIDataTable
                          title={false}
                          data={groupContacts}
                          columns={groupContactColumns}
                          options={{
                            selectableRows: "multiple",
                            onRowSelectionChange: (currentRowsSelected, allRowsSelected) => {
                              const objs = allRowsSelected
                                .map(({ dataIndex }) => groupContacts[dataIndex])
                                .filter(Boolean);
                              setSelectedContacts(objs);
                            },
                            customToolbarSelect: (selectedRows, displayData, setSelectedRows) => (
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                  width: "100%",
                                  p: 0.75,
                                }}
                              >
                                <TextField
                                  size="small"
                                  placeholder="Search…"
                                  value={searchText}
                                  onChange={(e) => setSearchText(e.target.value)}
                                  InputProps={{
                                    startAdornment: <SearchIcon fontSize="small" sx={{ mr: 0.75, opacity: 0.7 }} />,
                                  }}
                                  sx={{ maxWidth: 240 }}
                                />
                                <Tooltip title="Filters">
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      try {
                                        tableRef.current?.setState?.({ showFilter: true });
                                      } catch {}
                                    }}
                                  >
                                    <FilterListIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>

                                <Box sx={{ flex: 1 }} />

                                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                  {selectedContacts.length} selected
                                </Typography>

                                <Tooltip title="Clear selection">
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      setSelectedRows([]);
                                      setSelectedContacts([]);
                                    }}
                                  >
                                    <ClearIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            ),
                            onRowsDelete: () => false,
                            search: true,
                            searchText,
                            onSearchChange: (text) => setSearchText(text ?? ""),
                            filter: true,
                            print: false,
                            download: false,
                            viewColumns: true,
                            responsive: "standard",
                            pagination: true,
                            rowsPerPage: 25,
                            rowsPerPageOptions: [25, 50, 100],
                            elevation: 0,
                            tableBodyHeight: "240px",
                            tableBodyMaxHeight: "240px",
                            textLabels: {
                              body: {
                                noMatch: isLoadingContacts ? "Loading contacts..." : "No contacts found.",
                              },
                            },
                            setTableHeadRowProps: () => ({ sx: { height: 28 } }),
                            setTableHeadCellProps: () => ({
                              sx: { py: 0.25, px: 0.75, fontSize: 11, lineHeight: 1.2 },
                            }),
                            setTableProps: () => ({
                              size: "small",
                              sx: {
                                "& .MuiTableCell-root": { py: 0.4, px: 0.75, fontSize: 12 },
                                "& .MuiTableRow-root": { height: "28px" },
                                opacity: isLoadingContacts ? 0.5 : 1,
                              },
                            }),
                          }}
                          tableRef={(ref) => (tableRef.current = ref)}
                        />
                      </Box>
                    </>
                  )}

                  {tab === "Paste List" && (
                    <Box sx={{ ...subtleBoxSx, p: 1 }}>
                      <Typography variant="caption" sx={{ color: "text.secondary" }}>
                        Enter UK numbers, separated by commas or new lines
                      </Typography>
                      <textarea
                        placeholder="e.g. 07123456789, 07987654321"
                        onChange={(e) => setPasteList(e.target.value)}
                        value={pasteList}
                        style={{
                          marginTop: 6,
                          height: 120,
                          width: "100%",
                          border: "1px solid rgba(0,0,0,0.12)",
                          borderRadius: 8,
                          padding: 8,
                          resize: "vertical",
                          fontSize: 12,
                          lineHeight: 1.4,
                        }}
                      />
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Right: Compose */}
            <Grid item xs={12} md={6}>
              <Card sx={sectionCardSx}>
                <CardContent sx={sectionContentSx}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Compose
                  </Typography>

                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                    <FormControl fullWidth>
                      <Typography variant="caption" sx={labelSx}>
                        Sender
                      </Typography>
                      <Select size="small" value={sender ?? ""} onChange={(e) => setSender(e.target.value)}>
                        {senders.map((s) => (
                          <MenuItem key={s.number} value={s.number}>
                            {s.number}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>


                    <FormControl fullWidth>
                      <Typography variant="caption" sx={labelSx}>
                        Previous messages
                      </Typography>
                      <Select size="small" value={template || ""} onChange={handleTemplateChange} displayEmpty>
                        {templates.map((t) => (
                          <MenuItem key={t.id} value={t.id}>
                            {t.message}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Stack>

                  {tab === "Groups" && (
                    <>
                      <Divider />
                      <Box sx={{ mt: 0.5 }}>
                        <Typography
                          variant="overline"
                          sx={{
                            opacity: 0.75,
                            letterSpacing: 0.3,
                            fontSize: 9,
                            lineHeight: 1,
                            mb: 0.25,
                          }}
                        >
                          Client fields
                        </Typography>
                        <Stack
                          direction="row"
                          spacing={0.5}
                          sx={{
                            py: 0.25,
                            flexWrap: "wrap",
                            alignItems: "center",
                            rowGap: 0.25,
                          }}
                        >
                          {MERGE_TOKENS.map((token) => (
                            <Chip
                              size="small"
                              key={token}
                              label={token}
                              variant="outlined"
                              onClick={() => handleMergeInsert(token)}
                              sx={{
                                borderStyle: "dashed",
                                fontWeight: 600,
                                "&:hover": { boxShadow: 1 },
                                height: 22,
                              }}
                            />
                          ))}
                        </Stack>
                      </Box>
                    </>
                  )}
                  <Box sx={{ mt: 0.5 }}>
                    <Typography
                      variant="overline"
                      sx={{
                        opacity: 0.75,
                        letterSpacing: 0.3,
                        fontSize: 9,
                        lineHeight: 1,
                        mb: 0.25,
                      }}
                    >
                      My fields
                    </Typography>
                    <Stack
                      direction="row"
                      spacing={0.5}
                      onMouseDown={(e) => e.preventDefault()} 
                      sx={{
                        py: 0.25,
                        flexWrap: "wrap",
                        alignItems: "center",
                        rowGap: 0.25,
                      }}
                    >
                      <EditMergeFields
                        messageRef={messageRef}
                        value={messageBody}
                        setValue={handleMergeInsert}           
                        maxChars={320}
                        onTooLong={(m) => sendNotification({ msg: m })}
                      />
                    </Stack>
                  </Box>
                  <Divider />
                  <FormControl fullWidth>
                    <Typography variant="caption" sx={labelSx}>
                      Message
                    </Typography>
                    <textarea
                      placeholder={"Type your message"}
                      ref={messageRef}
                      onChange={handleMessageBodyChange}
                      onFocus={(e) => setActiveEl(e.target)}     // NEW
                      value={messageBody}
                      style={{
                        height: 120,
                        width: "100%",
                        border: "1px solid rgba(0,0,0,0.12)",
                        borderRadius: 8,
                        padding: 8,
                        resize: "vertical",
                        fontSize: 13,
                        lineHeight: 1.45,
                      }}
                    />
                    <Typography variant="caption" sx={{ color: "text.secondary", mt: 0.25, fontSize: 11 }}>
                      {messageBody.length} / {maxCharCount} {channel === "RCS" ? "(RCS)" : "(SMS)"}
                    </Typography>
                  </FormControl>

                  {channel === "RCS" && (
                    <>
                      <Divider />
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <Typography variant="caption" sx={{ ...labelSx, mb: 0 }}>
                          Button Suggestions
                        </Typography>
                        <Tooltip title="Add RCS button">
                          <span>
                            <Button
                              size="small"
                              startIcon={<AddCircleOutlineIcon fontSize="small" />}
                              onClick={addRcsButton}
                              disabled={rcsButtons.length >= RCS_MAX_BUTTONS}
                              sx={{ textTransform: "none" }}
                            >
                              Add button
                            </Button>
                          </span>
                        </Tooltip>
                      </Box>

                      <Stack spacing={0.75}>
                        {rcsButtons.map((b) => (
                          <Box
                            key={b.id}
                            sx={{
                              ...subtleBoxSx,
                              p: 0.75,
                              display: "grid",
                              gridTemplateColumns: "160px 1fr 1fr auto",
                              gap: 0.75,
                              alignItems: "center",
                            }}
                          >
                            {/* Type */}
                            <FormControl size="small">
                              <Select
                                value={b.type}
                                onChange={(e) => {
                                  const nextType = e.target.value;
                                  if (nextType === "REPLY") {
                                    updateRcsButton(b.id, { type: "REPLY", postbackData: "" });
                                  } else if (nextType === "DIAL_PHONE") {
                                    updateRcsButton(b.id, { type: "DIAL_PHONE", phoneNumber: "" });
                                  } else {
                                    updateRcsButton(b.id, { type: "OPEN_URL", url: "" });
                                  }
                                }}
                              >
                                <MenuItem value="DIAL_PHONE">
                                  <PhoneIcon sx={{ mr: 1 }} fontSize="small" /> Call number
                                </MenuItem>
                                <MenuItem value="OPEN_URL">
                                  <LinkIcon sx={{ mr: 1 }} fontSize="small" /> Open URL
                                </MenuItem>
                              </Select>
                            </FormControl>

                            {/* Text */}
                            <TextField
                              label="Button text (1–25)"
                              size="small"
                              value={b.text}
                              inputProps={{ maxLength: 25 }}
                              onFocus={(e) => setActiveEl(e.target)}     
                              onChange={(e) => updateRcsButton(b.id, { text: e.target.value })}
                            />

                            {b.type === "REPLY" && (
                              <TextField
                                label="Postback data (1–2048)"
                                size="small"
                                value={b.postbackData || ""}
                                inputProps={{ maxLength: 2048 }}
                                onFocus={(e) => setActiveEl(e.target)}    
                                onChange={(e) => updateRcsButton(b.id, { postbackData: e.target.value })}
                              />
                            )}
                            {b.type === "DIAL_PHONE" && (
                              <TextField
                                label="Phone number (E.164)"
                                size="small"
                                value={b.phoneNumber || ""}
                                placeholder="+447911123456"
                                onFocus={(e) => setActiveEl(e.target)}
                                onChange={(e) => updateRcsButton(b.id, { phoneNumber: e.target.value })}
                              />
                            )}
                            {b.type === "OPEN_URL" && (
                              <TextField
                                label="URL (https://...)"
                                size="small"
                                value={b.url || ""}
                                placeholder="https://example.com"
                                onFocus={(e) => setActiveEl(e.target)}
                                onChange={(e) => updateRcsButton(b.id, { url: e.target.value })}
                              />
                            )}

                            {/* Remove */}
                            <Tooltip title="Remove button">
                              <IconButton onClick={() => removeRcsButton(b.id)}>
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        ))}
                      </Stack>
                    </>
                  )}

                  <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                    <Button
                      size="small"
                      onClick={handleSend}
                      endIcon={isSubmitting ? <Spinner animation="border" size="sm" /> : <SendIcon fontSize="small" />}
                      variant="contained"
                      disableElevation
                      disabled={isSubmitting}
                      sx={{ borderRadius: 1.5, px: 1.25, py: 0.5, textTransform: "none", fontWeight: 700 }}
                    >
                      {!isSubmitting ? "Send" : "Sending..."}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card sx={sectionCardSx}>
                <CardContent sx={sectionContentSx}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Recent messages
                  </Typography>
                  <DataGrid
                    rows={messages.map((m) => ({ ...m, id: m.id }))}
                    columns={messageColumns}
                    pageSizeOptions={[5, 10, 25]}
                    initialState={{ pagination: { paginationModel: { pageSize: 5 } } }}
                    density="compact"
                    autoHeight
                    onRowClick={(params) => goToMessage(params.id)}
                    sx={{
                      cursor: "pointer",
                      "& .MuiDataGrid-row:hover": { backgroundColor: "action.hover" },
                      "& .MuiDataGrid-columnHeaders": { minHeight: 34, height: 34 },
                      "& .MuiDataGrid-cell": { py: 0.5, fontSize: 12 },
                    }}
                  />
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>

        <Alert
          severity="info"
          sx={{
            mb: 1.5,
            borderRadius: 1.5,
            py: 0.75,
            "& a": { textDecoration: "underline" },
          }}
        >
          To send SMS messages, use the groups synced from{" "}
          <a
            href="https://vetsnowteam.myfreshworks.com/crm/sales/contacts/view/30001934561?per_page=25"
            target="_blank"
            rel="noopener noreferrer"
          >
            Freshsales
          </a>
          . Alternatively, you can paste numbers. Guide{" "}
          <a
            href="https://servicedesk.vets-now.com/a/solutions/articles/50000201510"
            target="_blank"
            rel="noopener noreferrer"
          >
            here
          </a>
          .
        </Alert>

       <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
  <DialogTitle sx={{ pb: 0.5 }}>
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
      <Typography component="span" sx={{ fontSize: 16, fontWeight: 700 }}>
        Are you sure you want to send this message to{" "}
        {tab === "Groups"
          ? `${selectedContacts.length} recipient(s)?`
          : `${
              (pasteList || "")
                .split(pasteList.includes(",") ? "," : "\n")
                .map((row) => row.trim())
                .filter(Boolean).length
            } recipient(s)?`}
      </Typography>

    </Box>
  </DialogTitle>

  <DialogContent sx={{ pt: 1 }}>
    <Card
      sx={{
        borderRadius: 2,
        bgcolor: "primary.main",
        color: "#fff",
        mb: 1,
        p: 1,
      }}
    >
      <CardContent sx={{ p: 1, "&:last-child": { pb: 1 } }}>
        <Typography variant="body2" component="p" sx={{ whiteSpace: "pre-wrap", mb: rcsButtons.length ? 0.75 : 0 }}>
          {messageBody}
        </Typography>

        {channel === "RCS" && rcsButtons?.length > 0 && (
          <RcsSuggestions suggestions={rcsButtons} />
        )}
      </CardContent>
    </Card>

    <Alert severity="info" sx={{ borderRadius: 1.5, py: 1, px: 1.25 }}>
      Replies are not tracked, make sure you include information on how to reply
    </Alert>
  </DialogContent>

  <DialogActions sx={{ px: 2, pb: 1.5 }}>
    <Button onClick={() => setConfirmOpen(false)} color="secondary" size="small" disabled={isSubmitting}>
      Cancel
    </Button>
    <Button
      variant="contained"
      onClick={handleSubmit}
      endIcon={<SendIcon fontSize="small" />}
      size="small"
      disabled={isSubmitting}
      sx={{ borderRadius: 1.5, px: 1.25, py: 0.5, textTransform: "none", fontWeight: 700 }}
    >
      {isSubmitting ? "Sending..." : "Send"}
    </Button>
  </DialogActions>
</Dialog>

      </AccessContainer>
    </>
  );
};

export default Page;